import { ReactNode } from "react"
import { Box, ButtonBase, Collapse, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../modules/localization/translations"

const useStyles = makeStyles()((theme) => ({
    section: {
        marginTop: "1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        overflow: "hidden",
    },
    sectionHeader: {
        display: "flex",
        width: "100%",
        padding: "1rem",
        background: theme.palette.tailwind.slate[50],
    },
    sectionIcon: {
        color: theme.palette.tailwind.slate[500],
    },
    sectionTitle: {
        color: theme.palette.tailwind.slate[800],
    },
    sectionCount: {
        minWidth: "1.5rem",
        height: "1.5rem",
        padding: "0 .5rem",
        borderRadius: "1rem",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[600],
    },
    chevron: {
        color: theme.palette.tailwind.slate[400],
    },
}))

type Props = Readonly<{
    icon: IconDefinition
    titleId: TranslationKey
    titleFallback: string
    count?: number
    badge?: ReactNode
    open: boolean
    onToggle: () => void
    children: ReactNode
}>

export function DrawerSection({ icon, titleId, titleFallback, count, badge, open, onToggle, children }: Props) {
    const { classes } = useStyles()

    return (
        <Box className={classes.section}>
            <ButtonBase className={classes.sectionHeader} onClick={onToggle}>
                <Stack direction="row" alignItems="center" spacing=".75rem" width="100%">
                    <FontAwesomeIcon icon={icon} className={classes.sectionIcon} />
                    <LocalizedStrict id={titleId}>
                        <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            letterSpacing=".03em"
                            textTransform="uppercase"
                            className={classes.sectionTitle}
                        >
                            {titleFallback}
                        </Typography>
                    </LocalizedStrict>
                    {count !== undefined && (
                        <Stack alignItems="center" justifyContent="center" className={classes.sectionCount}>
                            <Typography component="span" variant="caption" fontWeight={700}>
                                {count}
                            </Typography>
                        </Stack>
                    )}
                    {badge}
                    <Box flexGrow={1} />
                    <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className={classes.chevron} />
                </Stack>
            </ButtonBase>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {children}
            </Collapse>
        </Box>
    )
}
